import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  translate,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { FormBaseComponent } from '@anihis/shared/src/shared/base-components/base-components';
import { LoginStore } from './login.store';
import { ActivatedRoute, Router } from '@angular/router';
import { RegexPatterns } from '@anihis/shared/src/constants/regex-patterns';
import { MatError } from '@angular/material/form-field';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { RouterLink } from '@angular/router';
import { LoadingStatus } from '../../shared/enums/loading';
import { EnterActionDirective } from '../../shared/directive/enter-action.directive';
import { SUPPORT_CONTACT } from '@anihis/shared';

@Component({
  selector: 'anihis-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslocoModule,
    MatError,
    MatSnackBarModule,
    MatButtonModule,
    RouterLink,
    EnterActionDirective,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [LoginStore],
})
export class LoginComponent extends FormBaseComponent {
  store = inject(LoginStore);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private snackBar = inject(MatSnackBar);
  private transloco = inject(TranslocoService);
  readonly supportEmail = SUPPORT_CONTACT.email;

  readonly status = LoadingStatus;
  invalidLogin = signal(false);
  showValidationErrors = signal(false);
  showPassword = signal(false);

  readonly onEnterLogin = () => this.login();

  private returnUrl: string | null = null;

  constructor() {
    super();
    this.initializeForm();

    // Get returnUrl from query params
    this.route.queryParams.subscribe((params) => {
      this.returnUrl = params['returnUrl'] || '/';
    });
  }

  private initializeForm() {
    this.form = this.fb.group({
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          Validators.pattern(RegexPatterns.EMAIL_REGEX),
        ],
      ],
      password: [
        '',
        [
          Validators.required,
          Validators.pattern(RegexPatterns.PASSWORD_STRENGTH_REGEX),
        ],
      ],
    });
  }

  redirectToForgotPassword() {
    this.router.navigate(['/forgot-password']);
  }

  redirectToActivateAccount() {
    this.router.navigateByUrl('/activate-account');
  }

  login() {
    if (this.store.status() === this.status.Loading) return;
    this.showValidationErrors.set(true);

    if (this.checkFormValidity()) return;

    this.invalidLogin.set(false);
    this.store.resetError();

    this.store
      .login(this.form.getRawValue(), this.returnUrl || undefined)
      .subscribe({
        next: () => {
          this.invalidLogin.set(false);
          this.form.reset();
          this.showValidationErrors.set(false);
        },
        error: (err: unknown) => {
          this.invalidLogin.set(true);
          const message =
            err instanceof Error
              ? err.message
              : this.transloco.translate('Login failed');

          const snack = this.snackBar.open(translate(message), '✕', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['snackbar-error'],
          });

          snack.onAction().subscribe(() => {
            snack.dismiss();
          });
        },
      });
  }
}
